import { useEffect, useState } from "react";
import useSWR from "swr";
import { Check, FileText, Loader2 } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { ApiError, swrFetcher, updateProject, type ProjectInfo } from "@/lib/api";
import { EmptyState } from "@/components/ui/empty-state";

interface BriefFields {
  audience: string;
  objective: string;
  visual_language: string;
  constraints: string;
}

const EMPTY: BriefFields = { audience: "", objective: "", visual_language: "", constraints: "" };

function briefFrom(project: ProjectInfo | undefined): BriefFields {
  const brief = project?.settings?.brief;
  if (!brief || typeof brief !== "object") return EMPTY;
  const record = brief as Record<string, unknown>;
  return {
    audience: typeof record.audience === "string" ? record.audience : "",
    objective: typeof record.objective === "string" ? record.objective : "",
    visual_language: typeof record.visual_language === "string" ? record.visual_language : "",
    constraints: typeof record.constraints === "string" ? record.constraints : "",
  };
}

export function ProjectBriefPanel({ open, onClose, projectId }: { open: boolean; onClose: () => void; projectId: string | null }) {
  const { data: projects, mutate } = useSWR<ProjectInfo[]>(open ? "/api/projects?include_archived=false" : null, swrFetcher);
  const project = projects?.find((item) => item.id === projectId);
  const [description, setDescription] = useState("");
  const [brief, setBrief] = useState<BriefFields>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDescription(project?.description ?? "");
    setBrief(briefFrom(project));
    setSaved(false);
    setError(null);
    // Depend on the project ID, not the object: SWR's focus revalidation returns
  // a new object identity for unchanged data, which reset the form and threw
  // away whatever the user had typed.
}, [open, project?.id]);

  const save = async () => {
    if (!project) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await updateProject(project.id, {
        description,
        settings: { ...project.settings, brief },
      });
      await mutate();
      setSaved(true);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not save the project brief");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Project Brief" placement="right" className="sm:max-w-md">
      <div className="flex-1 overflow-y-auto p-4 sm:p-5">
        {!projectId ? (
          <EmptyState
            icon={FileText}
            size="md"
            className="min-h-72"
            title="Choose a project first"
            hint="The brief is saved to the active project and stays available across generations."
          />
        ) : !project ? (
          <div className="flex min-h-72 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-studio-muted" /></div>
        ) : (
          <>
            <div className="mb-5 rounded-xl border border-studio-border bg-studio-card p-3.5">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-studio-muted">Active project</p>
              <p className="mt-1 text-sm font-semibold text-studio-ink">{project.name}</p>
              <p className="mt-1 text-[11px] leading-4 text-studio-muted">Use this as the shared production context for the project.</p>
            </div>

            <BriefField label="Production summary" value={description} onChange={setDescription} rows={5} placeholder="What are we making, and what must the result communicate?" />
            <BriefField label="Objective" value={brief.objective} onChange={(value) => setBrief((current) => ({ ...current, objective: value }))} placeholder="Campaign goal or story outcome" />
            <BriefField label="Audience" value={brief.audience} onChange={(value) => setBrief((current) => ({ ...current, audience: value }))} placeholder="Who should this resonate with?" />
            <BriefField label="Visual language" value={brief.visual_language} onChange={(value) => setBrief((current) => ({ ...current, visual_language: value }))} rows={4} placeholder="Tone, palette, framing and references" />
            <BriefField label="Constraints" value={brief.constraints} onChange={(value) => setBrief((current) => ({ ...current, constraints: value }))} rows={4} placeholder="Brand rules, exclusions or delivery notes" />
            {error && <p role="alert" className="mb-3 rounded-lg bg-studio-danger/10 px-3 py-2 text-xs text-studio-danger">{error}</p>}
          </>
        )}
      </div>
      {project && (
        <div className="border-t border-studio-border p-4 sm:p-5">
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving}
            className="flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-studio-accent px-4 text-xs font-semibold text-studio-accent-fg hover:bg-studio-accent-hover disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {saving ? "Saving…" : saved ? "Saved" : "Save brief"}
          </button>
        </div>
      )}
    </Dialog>
  );
}

function BriefField({ label, value, onChange, placeholder, rows = 3 }: { label: string; value: string; onChange: (value: string) => void; placeholder: string; rows?: number }) {
  return (
    <label className="mb-4 block">
      <span className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.14em] text-studio-muted">{label}</span>
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="w-full resize-y rounded-xl border border-studio-border bg-studio-card px-3.5 py-3 text-xs leading-5 text-studio-ink outline-none transition-colors placeholder:text-studio-muted focus:border-studio-accent/55"
      />
    </label>
  );
}
